// Shared global imagery for the scaled Earth and its metre-scale terrain.
// NASA GIBS Landsat WELD: CRS84, 512px tiles at every level.
// The streamer itself is body-generic (planetimagery.js); this is the Earth's
// instance, under the names the viewer, the terrain and the tests import.
import * as THREE from 'three';
import { EARTH_IMAGERY_SOURCE, EARTH_IMAGERY_PATH, EARTH_IMAGERY_CACHE, earthImageryLocalMax, earthImageryTileVersion } from '../data/earthImagerySource.js';
import { createPlanetImagery, IMAGERY_FADE_SECONDS, updateImageryTransitions } from './planetimagery.js';
import { createS2Composer } from './earthdetail.js';
import { RUNNING_ON_MOBILE } from '../mobiledevice.js';
import { mobileTextureUrl } from '../textureFormat.js';

// Use the same composite at every level: mixing Blue Marble parents with
// Landsat children produces visible colour rectangles at the LOD boundary.
// The one exception is the finest level: Landsat stops at 31 m (level 11),
// and level 12 (15 m) is Sentinel-2 cloudless composed on this grid and
// colour-fitted to the Landsat tile above it (earthdetail.js).
const LANDSAT_MAX_LEVEL = 11;
const S2_PATH = '/textures/earth-tiles/s2cloudless-2016';
let earth = null;
// The paid screensaver ships only original or NASA imagery, so it stops at
// Landsat (its shows never come close enough to reach level 12 anyway).
const SCREENSAVER = typeof location !== 'undefined' && new URLSearchParams(location.search).get('mode') === 'screensaver';
// A site build holds Landsat up to EARTH_IMAGERY_SHIPPED_MAX, so finer tiles
// go straight to NASA (patched ones too, though their url is the local copy).
// The dev server's tile route serves every level from its disk cache, and a
// desktop build may bundle more (earthImageryLocalMax).
const LOCAL_MAX = earthImageryLocalMax(typeof import.meta.env !== 'undefined' ? import.meta.env : {});
const compose = SCREENSAVER ? null : createS2Composer({
  tileSize: 512,
  imageryTile: (z, x, y) => earth.imageryTile(z, x, y),
  fetchTile: (tile, signal) => earth.fetchImageryTile(tile, signal),
});
earth = createPlanetImagery({
  name: 'earth', radius: 6371000, tileSize: 512, rootSpan: 288, rootLevel: 3, pageLevel: 8, maxLevel: SCREENSAVER ? LANDSAT_MAX_LEVEL : 12,
  mask: true, cacheName: EARTH_IMAGERY_CACHE, localMax: LOCAL_MAX,
  // A phone draws about a sixth of a desktop's pixels at its pixel ratio of
  // 1, so it selects at most 20 leaves into a 6 x 6 atlas (48 MB with its
  // mips, against 85 MB for the 8 x 8); docs/mobile-memory.md.
  ...(RUNNING_ON_MOBILE ? { atlasSide: 6, maxBudget: 20 } : {}),
  // The 1999 composite's scene seams (light and dark strips across the
  // Amazon, the Sahel, the outback) taken out against the seamless day map
  // between 8 and 390 km (scripts/build-earth-imagery-gain.mjs). Bump the
  // version when the file is rebuilt (and its phone copy,
  // scripts/build-mobile-textures.mjs).
  gain: { url: mobileTextureUrl('/textures/earth_imagery_gain.webp?v=2') },
  composeFrom: LANDSAT_MAX_LEVEL + 1, compose,
  localUrl,
  // A patched tile never falls back to NASA's copy, which still has the stray
  // scene: every one is bundled (scripts/patch-earth-tiles.mjs).
  url: (z, x, y) => z > LANDSAT_MAX_LEVEL ? `sentinel2:${z}/${y}/${x}`
    : earthImageryTileVersion(z, x, y) ? localUrl(z, x, y) : `${EARTH_IMAGERY_SOURCE}/${z}/${y}/${x}.jpeg`
});
// Tiles a patch changed (EARTH_IMAGERY_PATCHES) carry its version, so Cache
// Storage and the HTTP cache (a year, immutable) miss the old bytes; the
// level-12 tiles under them too, as they are colour-fitted to the patched
// level 11. A composed tile's name carries the composition's version
// (S2_COMPOSE_VERSION), so a browser holding tiles from an older fit
// composes them again: version 2 is the place-wise fit (earthdetail.js).
const S2_COMPOSE_VERSION = 2;
function localUrl(z, x, y) {
  return (z > LANDSAT_MAX_LEVEL ? `${S2_PATH}/${z}/${y}/${x}.v${S2_COMPOSE_VERSION}.jpeg` : `${EARTH_IMAGERY_PATH}/${z}/${y}/${x}.jpeg`) + earthImageryTileVersion(z, x, y);
}

export const TILE_SIZE = earth.TILE_SIZE;
export const ATLAS_SIDE = earth.ATLAS_SIDE;
export const PAGE_WIDTH = earth.PAGE_WIDTH;
export const PAGE_HEIGHT = earth.PAGE_HEIGHT;
export const MAX_LEVEL = earth.MAX_LEVEL;
// Sparse second-level pages: each 2.25-degree root cell can point to a
// 16x16 table of 31.25m tiles. Only selected regions allocate a table.
export const DETAIL_SIDE = earth.DETAIL_SIDE;
export { IMAGERY_FADE_SECONDS, updateImageryTransitions };
export const imageryTile = earth.imageryTile;
export const tileAt = earth.tileAt;
export const fetchImageryTile = earth.fetchImageryTile;
export const selectImageryTiles = earth.selectImageryTiles;
export const fillImageryPages = earth.fillImageryPages;
export const imageryFade = earth.imageryFade;
export const earthImageryUniforms = earth.uniforms;
export const EARTH_IMAGERY_GLSL = earth.glsl;
export const getEarthImageryStream = earth.getStream;

// The water mask the pages keep off the sea: the app's Earth's ocean texture
// (R is its coverage, src/shaders/earthocean.js), or the older 2K mask until
// that has loaded. Rocketry's far sea reads its depth and Kd490 through the
// same uniform (terrain.js).
export function earthOceanMaskTexture(u) {
  if (!u) return null;
  return u.oceanTextureOn?.value ? u.oceanTexture.value : u.specularTexture?.value || null;
}

// Flight adapter: the streamer itself knows nothing about gameplay.
export function createGlobalEarthImagery(G) {
  const stream = getEarthImageryStream(G.ctx.renderer);
  const position = new THREE.Vector3(), spin = new THREE.Quaternion(), orientation = new THREE.Quaternion();
  const off = [G.events.on('exit', stream.cancel), G.events.on('frame', ({ now }) => {
    if (!G.R.active || G.R.viewBody !== 'earth' || G.R.mode === 'vab') {
      stream.cancel();
      return;
    }
    const p = G.bodies.inertialToSurface('earth', G.camInertial);
    position.set(p.x, p.y, p.z);
    G.bodies.spinQuaternion('earth', spin).invert();
    orientation.copy(spin).multiply(G.localCamera.quaternion);
    stream.update({ now, position, orientation, lens: G.localCamera.projectionMatrix,
      oceanMask: earthOceanMaskTexture(G.bodies.get('earth').spinMesh?.material?.uniforms),
      budget: G.settings.tier().earthTiles });
  })];
  G.earthImagery = stream;
  return { stats: stream.stats, dispose() { off.forEach(fn => fn()); stream.cancel(); } };
}
